// src/lib/services/wellnessService.ts
import { supabase } from '../../supabaseClient';
import { getCurrentLocalDate, getLocalDateDaysAgo } from '../../utils/date';
import type {
  WaterEntry,
  WeightEntry,
  WeightGoal,
  WellnessSettings,
  BreathingSession,
  MigrationResults,
} from '../../../types';

interface WellnessService {
  // Weight tracking methods
  getWeightEntries(limit?: number): Promise<WeightEntry[]>;
  addWeightEntry(weight: number, date?: string | null, notes?: string): Promise<WeightEntry>;
  updateWeightEntry(entryId: string, updates: Partial<WeightEntry>): Promise<WeightEntry>;
  deleteWeightEntry(entryId: string): Promise<void>;

  // Weight goals methods
  getActiveWeightGoal(): Promise<WeightGoal | null>;
  setWeightGoal(goalWeight: number, targetDate?: string | null): Promise<WeightGoal>;

  // Water tracking methods
  getTodaysWaterEntry(): Promise<WaterEntry | null>;
  getWaterEntries(days?: number): Promise<WaterEntry[]>;
  updateWaterIntake(
    glassesConsumed: number,
    glassSize?: number,
    dailyGoal?: number
  ): Promise<WaterEntry>;
  addWaterGlasses(additionalGlasses: number): Promise<WaterEntry>;
  resetDailyWater(): Promise<WaterEntry>;

  // Breathing sessions methods
  getBreathingSessions(days?: number): Promise<BreathingSession[]>;
  startBreathingSession(
    patternName: string,
    patternConfig?: object | null,
    cyclesPlanned?: number
  ): Promise<BreathingSession>;
  completeBreathingSession(
    sessionId: string,
    actualCycles: number,
    sessionDuration: number,
    technique?: string
  ): Promise<BreathingSession>;

  // Settings methods
  getWellnessSettings(): Promise<WellnessSettings | null>;
  updateWellnessSettings(settings: Partial<WellnessSettings>): Promise<WellnessSettings>;

  // Migration methods
  migrateLocalStorageData(): Promise<MigrationResults>;
  migrateLocalStorageDataForUser(userId: string): Promise<MigrationResults>;
}

export const wellnessService: WellnessService = {
  // ===================================
  // WEIGHT TRACKING
  // ===================================

  async getWeightEntries(limit: number = 50): Promise<WeightEntry[]> {
    const { data, error } = await supabase
      .from('weight_entries')
      .select('*')
      .order('date', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data || []) as WeightEntry[];
  },

  async addWeightEntry(
    weight: number,
    date: string | null = null,
    notes: string = ''
  ): Promise<WeightEntry> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('weight_entries')
      .insert([
        {
          user_id: user.id,
          weight: parseFloat(weight.toString()),
          date: date ?? getCurrentLocalDate(),
          notes,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as WeightEntry;
  },

  async updateWeightEntry(entryId: string, updates: Partial<WeightEntry>): Promise<WeightEntry> {
    const { data, error } = await supabase
      .from('weight_entries')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', entryId)
      .select()
      .single();

    if (error) throw error;
    return data as WeightEntry;
  },

  async deleteWeightEntry(entryId: string): Promise<void> {
    const { error } = await supabase.from('weight_entries').delete().eq('id', entryId);

    if (error) throw error;
  },

  // Weight Goals
  async getActiveWeightGoal(): Promise<WeightGoal | null> {
    const { data, error } = await supabase
      .from('weight_goals')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data as WeightGoal | null;
  },

  async setWeightGoal(goalWeight: number, targetDate: string | null = null): Promise<WeightGoal> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    // Deactivate existing goals
    await supabase.from('weight_goals').update({ is_active: false }).eq('user_id', user.id);

    const { data, error } = await supabase
      .from('weight_goals')
      .insert([
        {
          user_id: user.id,
          goal_weight: parseFloat(goalWeight.toString()),
          target_date: targetDate,
          is_active: true,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as WeightGoal;
  },

  // ===================================
  // WATER TRACKING
  // ===================================

  async getTodaysWaterEntry(): Promise<WaterEntry | null> {
    const today = getCurrentLocalDate();
    const { data, error } = await supabase
      .from('water_entries')
      .select('*')
      .eq('date', today)
      .maybeSingle();

    if (error) throw error;
    return data as WaterEntry | null;
  },

  async getWaterEntries(days: number = 7): Promise<WaterEntry[]> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const startDate = getLocalDateDaysAgo(days);

    const { data, error } = await supabase
      .from('water_entries')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startDate)
      .order('date', { ascending: false });

    if (error) throw error;
    return (data || []) as WaterEntry[];
  },

  async updateWaterIntake(
    glassesConsumed: number,
    glassSize: number = 8,
    dailyGoal: number = 8
  ): Promise<WaterEntry> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const today = getCurrentLocalDate();

    const { data, error } = await supabase
      .from('water_entries')
      .upsert(
        {
          user_id: user.id,
          date: today,
          glasses_consumed: parseInt(glassesConsumed.toString()),
          glass_size_oz: parseInt(glassSize.toString()),
          daily_goal: parseInt(dailyGoal.toString()),
        },
        {
          onConflict: 'user_id,date',
        }
      )
      .select()
      .single();

    if (error) throw error;
    return data as WaterEntry;
  },

  async addWaterGlasses(additionalGlasses: number): Promise<WaterEntry> {
    const todaysEntry = await this.getTodaysWaterEntry();
    const currentGlasses = todaysEntry?.glasses_consumed ?? 0;
    const glassSize = todaysEntry?.glass_size_oz ?? 8;
    const dailyGoal = todaysEntry?.daily_goal ?? 8;

    return this.updateWaterIntake(currentGlasses + additionalGlasses, glassSize, dailyGoal);
  },

  async resetDailyWater(): Promise<WaterEntry> {
    return this.updateWaterIntake(0);
  },

  // ===================================
  // BREATHING SESSIONS
  // ===================================

  async getBreathingSessions(days: number = 7): Promise<BreathingSession[]> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const startDate = getLocalDateDaysAgo(days);

    const { data, error } = await supabase
      .from('breathing_sessions')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startDate)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as BreathingSession[];
  },

  async startBreathingSession(
    patternName: string,
    patternConfig: object | null = null,
    cyclesPlanned: number = 5
  ): Promise<BreathingSession> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('breathing_sessions')
      .insert([
        {
          user_id: user.id,
          pattern_name: patternName,
          pattern_config: (patternConfig ?? {}) as never,
          cycles_planned: cyclesPlanned,
          date: getCurrentLocalDate(),
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as BreathingSession;
  },

  async completeBreathingSession(
    sessionId: string,
    actualCycles: number,
    sessionDuration: number,
    technique: string = ''
  ): Promise<BreathingSession> {
    const { data, error } = await supabase
      .from('breathing_sessions')
      .update({
        cycles_completed: actualCycles,
        session_duration_seconds: sessionDuration,
        technique,
        completed_at: new Date().toISOString(),
      })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data as BreathingSession;
  },

  // ===================================
  // WELLNESS SETTINGS
  // ===================================

  async getWellnessSettings(): Promise<WellnessSettings | null> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wellness_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) throw error;
    return data as WellnessSettings | null;
  },

  async updateWellnessSettings(settings: Partial<WellnessSettings>): Promise<WellnessSettings> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wellness_settings')
      .upsert(
        {
          user_id: user.id,
          ...settings,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'user_id',
        }
      )
      .select()
      .single();

    if (error) throw error;
    return data as WellnessSettings;
  },

  // ===================================
  // DATA MIGRATION FROM LOCALSTORAGE
  // ===================================

  async migrateLocalStorageData(): Promise<MigrationResults> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const migrationResults: MigrationResults = {
      weight: { migrated: 0, errors: 0 },
      water: { migrated: 0, errors: 0 },
      breathing: { migrated: 0, errors: 0 },
      settings: { migrated: 0, errors: 0 },
    };

    try {
      // Migrate weight entries
      const weightEntries = JSON.parse(localStorage.getItem('weightEntries') ?? '[]');
      for (const entry of weightEntries) {
        try {
          await this.addWeightEntry(entry.weight, entry.date);
          migrationResults.weight.migrated++;
        } catch (error) {
          console.error('Error migrating weight entry:', error);
          migrationResults.weight.errors++;
        }
      }

      // Migrate weight goal
      const goalWeight = localStorage.getItem('goalWeight');
      if (goalWeight) {
        try {
          await this.setWeightGoal(parseFloat(goalWeight));
          migrationResults.weight.migrated++;
        } catch (error) {
          console.error('Error migrating weight goal:', error);
          migrationResults.weight.errors++;
        }
      }

      // Migrate water data
      const weeklyWaterData = JSON.parse(localStorage.getItem('weeklyWaterData') ?? '[]');
      for (const entry of weeklyWaterData) {
        try {
          await supabase.from('water_entries').upsert(
            {
              user_id: user.id,
              date: entry.date,
              glasses_consumed: entry.intake,
              daily_goal: entry.goal,
              glass_size_oz: 8, // Default, can't get from localStorage
            },
            {
              onConflict: 'user_id,date',
            }
          );
          migrationResults.water.migrated++;
        } catch (error) {
          console.error('Error migrating water entry:', error);
          migrationResults.water.errors++;
        }
      }

      // Migrate settings
      const waterGoal = localStorage.getItem('waterGoal');
      const glassSize = localStorage.getItem('glassSize');

      if (waterGoal || glassSize) {
        try {
          await this.updateWellnessSettings({
            water_daily_goal: waterGoal ? parseInt(waterGoal) : 8,
            default_glass_size: glassSize ? parseInt(glassSize) : 8,
          });
          migrationResults.settings.migrated++;
        } catch (error) {
          console.error('Error migrating settings:', error);
          migrationResults.settings.errors++;
        }
      }

      // Clear localStorage after successful migration
      const keysToRemove = [
        'weightEntries',
        'goalWeight',
        'waterIntake',
        'waterGoal',
        'glassSize',
        'waterStreak',
        'weeklyWaterData',
        'lastWaterReset',
      ];

      keysToRemove.forEach(key => localStorage.removeItem(key));

      return migrationResults;
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    }
  },

  async migrateLocalStorageDataForUser(userId: string): Promise<MigrationResults> {
    const migrationResults: MigrationResults = {
      weight: { migrated: 0, errors: 0 },
      water: { migrated: 0, errors: 0 },
      breathing: { migrated: 0, errors: 0 },
      settings: { migrated: 0, errors: 0 },
    };

    try {
      // Migrate weight entries
      const weightEntries = JSON.parse(localStorage.getItem('weightEntries') ?? '[]');
      for (const entry of weightEntries) {
        try {
          const { error } = await supabase
            .from('weight_entries')
            .insert([
              {
                user_id: userId,
                weight: parseFloat(entry.weight),
                date: entry.date ?? getCurrentLocalDate(),
                notes: entry.notes ?? '',
              },
            ])
            .select()
            .single();

          if (error) throw error;
          migrationResults.weight.migrated++;
        } catch (error) {
          console.error('Error migrating weight entry:', error);
          migrationResults.weight.errors++;
        }
      }

      // Migrate weight goal
      const goalWeight = localStorage.getItem('goalWeight');
      if (goalWeight) {
        try {
          await supabase
            .from('weight_goals')
            .insert([
              {
                user_id: userId,
                goal_weight: parseFloat(goalWeight),
                is_active: true,
              },
            ])
            .select()
            .single();
          migrationResults.weight.migrated++;
        } catch (error) {
          console.error('Error migrating weight goal:', error);
          migrationResults.weight.errors++;
        }
      }

      // Migrate water data
      const weeklyWaterData = JSON.parse(localStorage.getItem('weeklyWaterData') ?? '[]');
      for (const entry of weeklyWaterData) {
        try {
          await supabase.from('water_entries').upsert(
            {
              user_id: userId,
              date: entry.date,
              glasses_consumed: entry.intake,
              daily_goal: entry.goal,
              glass_size_oz: 8, // Default, can't get from localStorage
            },
            {
              onConflict: 'user_id,date',
            }
          );
          migrationResults.water.migrated++;
        } catch (error) {
          console.error('Error migrating water entry:', error);
          migrationResults.water.errors++;
        }
      }

      // Migrate settings
      const waterGoal = localStorage.getItem('waterGoal');
      const glassSize = localStorage.getItem('glassSize');

      if (waterGoal || glassSize) {
        try {
          await supabase.from('wellness_settings').upsert(
            {
              user_id: userId,
              water_daily_goal: waterGoal ? parseInt(waterGoal) : 8,
              default_glass_size: glassSize ? parseInt(glassSize) : 8,
            },
            {
              onConflict: 'user_id',
            }
          );
          migrationResults.settings.migrated++;
        } catch (error) {
          console.error('Error migrating settings:', error);
          migrationResults.settings.errors++;
        }
      }

      // Clear localStorage after successful migration
      const keysToRemove = [
        'weightEntries',
        'goalWeight',
        'waterIntake',
        'waterGoal',
        'glassSize',
        'waterStreak',
        'weeklyWaterData',
        'lastWaterReset',
      ];

      keysToRemove.forEach(key => localStorage.removeItem(key));

      return migrationResults;
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    }
  },
};
